import { es } from '../i18n/es';
import { createEmptySave } from './defaults';
import { MigrationError, migrate, type Migration } from './migrations';
import { SAVE_KEY, SCHEMA_VERSION, type SaveFile } from './schema';
import type { StorageAdapter } from './storage-adapter';
import { validateSave, type SaveValidation } from './validate';

export const CORRUPT_KEY_PREFIX = 'narcoverse:save:corrupt:';

export interface SaveCorruptEvent {
  type: 'SAVE_CORRUPT';
  corruptKey: string;
  reason: string;
}

export interface LoadResult {
  save: SaveFile;
  /** Message for the interface when a damaged document was set aside; null otherwise. */
  notice: string | null;
  corruptKey?: string;
  /** Names of the migrations that ran on the stored document, in order (GDD §6.4). */
  migrated: string[];
}

export interface LoadOptions {
  prefersReducedMotion: boolean;
  /** ISO 8601 clock, injected so the copy key is deterministic in tests. */
  now: () => string;
  onEvent?: (event: SaveCorruptEvent) => void;
  /** Migration registry, target version and validator: injectable so tests can use fictitious versions. */
  migrations?: readonly Migration[];
  targetVersion?: number;
  validate?: (doc: unknown) => SaveValidation;
}

/**
 * Reads the save document (GDD §6.4). No document: an empty one, silently. A document that cannot be
 * parsed or does not follow the schema is never deleted: its raw text is copied to
 * `narcoverse:save:corrupt:<ISO>`, an empty document replaces it and the interface gets a notice.
 * Never throws because of what the storage holds.
 */
export function loadSave(adapter: StorageAdapter, options: LoadOptions): LoadResult {
  const empty = () => createEmptySave({ prefersReducedMotion: options.prefersReducedMotion });
  const raw = adapter.get(SAVE_KEY);
  if (raw === null) return { save: empty(), notice: null, migrated: [] };

  const checked = check(raw, options);
  if (checked.ok) return { save: checked.value, notice: null, migrated: checked.migrated };

  const corruptKey = `${CORRUPT_KEY_PREFIX}${options.now()}`;
  const save = empty();
  // If the copy cannot be written (quota), the storage adapter of #45 moves to memory and the original
  // stays untouched in the browser; the empty document then only lives for this session.
  adapter.set(corruptKey, raw);
  adapter.set(SAVE_KEY, JSON.stringify(save));
  options.onEvent?.({ type: 'SAVE_CORRUPT', corruptKey, reason: checked.reason });
  return { save, notice: es['ui.save.corrupt'], corruptKey, migrated: [] };
}

function check(
  raw: string,
  options: LoadOptions,
): { ok: true; value: SaveFile; migrated: string[] } | { ok: false; reason: string } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ok: false, reason: 'unparseable' };
  }
  let migrated: { doc: unknown; ran: string[] };
  try {
    migrated = migrate(parsed, options.migrations, options.targetVersion ?? SCHEMA_VERSION);
  } catch (error) {
    return { ok: false, reason: error instanceof MigrationError ? error.message : 'migration failed' };
  }
  const result = (options.validate ?? validateSave)(migrated.doc);
  if (result.ok) return { ok: true, value: result.value, migrated: migrated.ran };
  return { ok: false, reason: result.errors[0]?.path ?? '' };
}
